import type { Employee } from '@gokrew/models/employee';

export enum SegmentType {
  Clock = 'CLOCK',
  Break = 'BREAK',
}

export enum DayStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export enum RequestStatus {
  Pending = 'PENDING',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export enum WeekApprovalStatus {
  Pending = 'PENDING',
  Approved = 'APPROVED',
  AutoApproved = 'AUTO_APPROVED',
  Rejected = 'REJECTED',
}

export interface ApiTimestamps {
  createdAt: string;
  updatedAt: string;
}

export interface ApiSoftDeletable extends ApiTimestamps {
  deletedAt: string | null;
}

/** { fromDate, toDate } shape used in API query params */
export interface DateRange {
  fromDate: string;
  toDate: string;
}

/** Base fields shared by request segment payloads */
interface RequestSegment {
  id?: string;
  startedAt: string;
  endedAt: string;
  type: SegmentType;
  note?: string;
}

export interface ApiEntity {
  id: string;
}

export interface ApiCompanyEntity extends ApiEntity {
  companyId: string;
}

export interface ApiEmployeeEntity extends ApiCompanyEntity {
  employeeId: string;
}

export interface ApiBranchedEntity extends ApiEmployeeEntity {
  branchId: string;
}

/* ---------------------------------- */
/* API types */
/* ---------------------------------- */

export type GetHistoryParams = Partial<DateRange>;

export interface TimesheetSegment extends ApiEntity, ApiSoftDeletable {
  employeeId: string;
  dayId: string;
  type: SegmentType;
  startTime: string;
  endTime: string | null;
  isOvernightSplit: boolean;
  splitGroupId: string | null;
  entryTimezone: string;
  source: string;
  employeeNote: string | null;
  hrNote: string | null;
  autoClosed: boolean;
  createdByUserId: string;
  updatedByUserId: string;
}

export interface TimesheetDay extends ApiBranchedEntity, ApiSoftDeletable {
  shiftPolicyId: string;
  workDate: string;
  dayType: string;
  dayTargetId: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  expectedMin: number;
  breakAllowanceMin: number;
  maxWorkingMin: number;
  workedMin: number;
  status: DayStatus;
  submittedAt: string | null;
  isLocked: boolean;
  lockedByUserId: string | null;
  lockedAt: string | null;
  updatedByUserId: string;
  segments: TimesheetSegment[];
  weekApprovalStatus: WeekApprovalStatus | null;
}

export interface OpenSegment {
  type: SegmentType;
  startTime: string;
  endTime: string | null;
}

export interface TimesheetTodayData {
  day: TimesheetDay;
  openSegment: OpenSegment | null;
  workedSoFarSec: number;
  breakSoFarSec: number;
}

export interface TimesheetTodayResponse {
  message: string;
  data: TimesheetTodayData;
}

export interface TimesheetHistoryResponse {
  message: string;
  data: TimesheetDay[];
}

/** Scheduled vs worked minutes over a range, counting days not worked yet. */
export interface TimesheetHoursSummary {
  scheduledMin: number;
  completedMin: number;
  remainingMin: number;
  overtimeMin: number;
  /** Expected minutes per date (YYYY-MM-DD), including days not worked yet. */
  expectedMinByDate: Record<string, number>;
}

export interface TimesheetHoursSummaryResponse {
  message: string;
  data: TimesheetHoursSummary;
}

export interface GetRequestsParams {
  status?: RequestStatus;
  fromDate?: string;
  toDate?: string;
}

export type GetWeekApprovalsParams = DateRange;

export interface UpdateSegmentPayload {
  startedAt?: string;
  endedAt?: string;
  employeeNote?: string;
}

export interface PatchDaySegment {
  id?: string;
  startTime: string;
  endTime: string;
  type: SegmentType;
  entryTimezone: string;
  employeeNote?: string | null;
}

export interface PatchDayPayload {
  segments: PatchDaySegment[];
  deleteSegmentIds: string[];
}

export interface CreateRequestPayload {
  workDate: string;
  proposed: {
    segments: RequestSegment[];
    deleteSegmentIds: string[];
  };
  reason: string;
}

export interface WriteDayPayload {
  workDate: string;
  segments: PatchDaySegment[];
  deleteSegmentIds: string[];
  reason?: string;
}

export interface TimesheetStatus {
  enabled: boolean;
}

export interface TimesheetRequestRaw extends ApiEntity {
  workDate: string;
  createdAt: string;
  status: string;
  kind: string;
  reason?: string;
  decisionNote?: string | null;
  employee: Employee;
  proposed?: {
    resultSegments?: RequestSegment[];
    baseSegments?: RequestSegment[];
  };
}
